import { t } from '@lingui/macro';
import { useSeoMetaData } from 'hooks';
import { BrowseSeoType } from 'types/seoType';
import { RoutePaths } from 'types/enums';
import { useMatch } from 'react-router-dom';
import { useLingui } from '@lingui/react';
import { useStaticSeo } from 'hooks/useStaticSeo';
import { useDynamicBranding } from 'hooks/useDynamicBranding';

type SeoTemplate = {
    title: (translatedWildcard: string) => string;
    description: (lowerCaseWildcard: string) => string;
    header: (translatedWildcard: string) => string;
    byline: (lowerCaseWildcard: string) => string;
};

const useSeoTemplates = (): Record<string, SeoTemplate> => {
    const { name, samplesWithName } = useDynamicBranding();

    return {
        instruments: {
            title: (translatedWildcard: string) =>
                t`${translatedWildcard} Samples & Loops - 100% Royalty Free`,
            description: (lowerCaseWildcard: string) =>
                t`Download 100% royalty free ${lowerCaseWildcard} sample packs from leading labels featuring the best loops, beats and sounds for your music or creative projects. Get access to an expansive library of samples with your ${name} Studio subscription.`,
            header: (translatedWildcard: string) =>
                t`${translatedWildcard} Samples & Loops`,
            byline: (lowerCaseWildcard: string) =>
                t`Explore our collection 100% royalty-free ${lowerCaseWildcard} samples & loops. ${name}'s ${lowerCaseWildcard} sample packs are designed to inspire and elevate your music production, our high-quality sounds & beats provide endless creative possibilities across hundreds of genres.`,
        },
        genres: {
            title: (translatedWildcard: string) =>
                t`${translatedWildcard} Samples & Loops - 100% Royalty Free`,
            description: (lowerCaseWildcard: string) =>
                t`High quality & royalty free ${lowerCaseWildcard} sample packs from the world's top audio sample labels & producers. Find the ${lowerCaseWildcard} samples, loops, beats & one-shots you need for your music project at ${samplesWithName}.`,
            header: (translatedWildcard: string) =>
                t`${translatedWildcard} Samples & Loops`,
            byline: (lowerCaseWildcard: string) =>
                t`Explore our collection 100% royalty-free ${lowerCaseWildcard} samples & loops. ${name}'s ${lowerCaseWildcard} sample packs are designed to inspire and elevate your music production, our high-quality sounds & beats provide endless creative possibilities across hundreds of genres.`,
        },
        sfxs: {
            title: (translatedWildcard: string) =>
                t`Download ${translatedWildcard} Samples & Sound Effects - 100% Royalty Free`,
            description: (lowerCaseWildcard: string) =>
                t`Download royalty free ${lowerCaseWildcard} sound effects. Find high quality ${lowerCaseWildcard} sounds & samples for your next creative project with ${name} in MP3 or WAV format.`,
            header: (translatedWildcard: string) =>
                t`${translatedWildcard} Sound Effects`,
            byline: (lowerCaseWildcard: string) =>
                t`Explore our collection 100% royalty-free ${lowerCaseWildcard} samples & loops. ${name}'s ${lowerCaseWildcard} sample packs are designed to inspire and elevate your music production, our high-quality sounds & beats provide endless creative possibilities across hundreds of genres.`,
        },
    };
};

const useWildcardRouteMatch = () => {
    const instrumentsMatch = useMatch(`/${RoutePaths.Instruments}/:wildcard`);
    const genresMatch = useMatch(`/${RoutePaths.Genres}/:wildcard`);
    const sfxsMatch = useMatch(`/${RoutePaths.Sfxs}/:wildcard`);

    if (instrumentsMatch?.params.wildcard) {
        return {
            routeType: 'instruments' as const,
            wildcard: instrumentsMatch.params.wildcard,
        };
    }
    if (genresMatch?.params.wildcard) {
        return {
            routeType: 'genres' as const,
            wildcard: genresMatch.params.wildcard,
        };
    }
    if (sfxsMatch?.params.wildcard) {
        return {
            routeType: 'sfxs' as const,
            wildcard: sfxsMatch.params.wildcard,
        };
    }

    return null;
};

const useGetWildcardSeoFallback = () => {
    const { i18n } = useLingui();
    const seoTemplates = useSeoTemplates();
    const routeMatch = useWildcardRouteMatch();

    if (!routeMatch) {
        return {};
    }

    const { routeType, wildcard } = routeMatch;
    const translatedWildcard = i18n._(wildcard);
    const lowerCaseWildcard = translatedWildcard.toLowerCase();
    const template = seoTemplates[routeType];

    return {
        title: template.title(translatedWildcard),
        description: template.description(lowerCaseWildcard),
        header: template.header(translatedWildcard),
        byline: template.byline(lowerCaseWildcard),
    };
};

const useBrowseSeo = (): {
    loading: boolean;
    metaData: BrowseSeoType;
} => {
    const { loading, seoMetaData } = useSeoMetaData({ withWildcard: false });

    const wildcardSeoFallback = useGetWildcardSeoFallback();
    const { browse } = useStaticSeo();

    const metaData = {
        ...browse,
        ...wildcardSeoFallback,
        ...(seoMetaData?.title && { title: seoMetaData.title }),
        ...(seoMetaData?.description && {
            description: seoMetaData.description,
        }),
        ...(seoMetaData?.header && { header: seoMetaData.header }),
        ...(seoMetaData?.byline && { byline: seoMetaData.byline }),
    };

    return { loading, metaData };
};

export default useBrowseSeo;
